import { useTenderActionError } from "../../../../../hooks/useTenderActionError";
import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import style from "./ComplexOrderBids.module.scss";
import { Expeditor } from "../../../../../services";
import CustomInput from "../../../../custom/custom-input/CustomInput";
import CustomInputCurrency from "../../../../custom/custom-input-currency/CustomInputCurrency";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import CustomTextarea from "../../../../custom/custom-textarea/CustomTextarea";
import ConfirmActionModal from "../ConfirmActionModal/ConfirmActionModal";
import { TenderDeadlineText } from "../../../../shared/TenderDeadlineText/TenderDeadlineText";
import { isTransitDaysRequired } from "../../utils/transitDaysValidation";
import { isRailwayOrder } from "../../../../../pages/orders/utils/railwayOrder";

const formatShippingWindow = (order) => {
  const from = order?.shippingDate;
  const to = order?.shippingDateTo || order?.shippingDateEnd;

  if (!from && !to) return "-";
  if (from && !to) return moment.utc(from).format("DD.MM.YYYY");
  if (!from && to) return moment.utc(to).format("DD.MM.YYYY");

  return `${moment.utc(from).format("DD.MM.YYYY")} - ${moment.utc(to).format("DD.MM.YYYY")}`;
};

const ComplexOrderBids = ({ onRefreshOrder, order, onUpdateData, onReject }) => {
  const { t } = useTranslation();
  const transportContext = isRailwayOrder(order) ? "railway" : undefined;
  const { requestErrorDisplay } = useTenderActionError(order, { onRefreshOrder });
  const maxVehiclesCount = Number(order?.vehiclesCount) || 0;
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState(order?.acceptedCurrency || "KZT");
  const [vehiclesCount, setVehiclesCount] = useState(0);
  const [plannedDays, setPlannedDays] = useState("");
  const [comments, setComments] = useState("");
  const [errorText, setErrorText] = useState("");
  const [plannedDaysErrorText, setPlannedDaysErrorText] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const lockCurrency = Boolean(order?.acceptedCurrency);
  const shippingWindow = useMemo(() => formatShippingWindow(order), [order]);
  const transitDaysRequired = isTransitDaysRequired(order?.companyId);

  useEffect(() => {
    setPrice("");
    setCurrency(order?.acceptedCurrency || "KZT");
    setVehiclesCount(0);
    setPlannedDays("");
    setComments("");
    setErrorText("");
    setPlannedDaysErrorText("");
  }, [order?.id, order?.acceptedCurrency]);

  const decrementVehicles = () => {
    setVehiclesCount((current) => Math.max(0, Number(current) - 1));
  };

  const incrementVehicles = () => {
    setVehiclesCount((current) => {
      const nextValue = Number(current) + 1;
      return maxVehiclesCount ? Math.min(maxVehiclesCount, nextValue) : nextValue;
    });
  };

  const validate = () => {
    let isValid = true;
    const roundedPrice = Math.round(Number(price));

    if (!roundedPrice || roundedPrice <= 0) {
      setErrorText(t("complexTender.submitBid.priceRequired", { context: transportContext }));
      isValid = false;
    } else if (roundedPrice > 2000000000) {
      setErrorText(t("complexTender.submitBid.priceTooLarge"));
      toast.error(t("complexTender.submitBid.priceTooLarge"));
      isValid = false;
    }

    if (!vehiclesCount || vehiclesCount <= 0) {
      toast.error(t("complexTender.submitBid.vehiclesRequired", { context: transportContext }));
      isValid = false;
    } else if (maxVehiclesCount && vehiclesCount > maxVehiclesCount) {
      toast.error(t("complexTender.submitBid.maxVehicles", {
        count: maxVehiclesCount,
        context: transportContext,
      }));
      isValid = false;
    }

    if (transitDaysRequired && !plannedDays.trim()) {
      setPlannedDaysErrorText(t("orderInfoExpeditor.suggestPrice.transitDaysRequired"));
      isValid = false;
    }

    return isValid;
  };

  const handleSuggestClick = () => {
    if (!validate()) return;
    setShowConfirmModal(true);
  };

  const handleConfirmSend = async () => {
    if (!order?.id) return;

    setIsSubmitting(true);
    try {
      await Expeditor.sendComplexOrderBid(order.id, {
        price: Math.round(Number(price)),
        currency,
        vehiclesCount: Number(vehiclesCount),
        comments,
        plannedDays: String(plannedDays),
      });
      toast.success(t("complexTender.submitBid.bidSent"));
      setPrice("");
      setVehiclesCount(0);
      setPlannedDays("");
      setComments("");
      setShowConfirmModal(false);
      if (onUpdateData) onUpdateData();
    } catch (error) {
      console.error(error);
      await requestErrorDisplay(error, t, "complexTender.submitBid.sendBidError");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!order?.id) return;

    setIsRejecting(true);
    try {
      await Expeditor.rejectTender(order.id);
      toast.success(t("complexTender.submitBid.rejectTenderSuccess"));
      setShowRejectModal(false);
      if (onReject) onReject();
      if (onUpdateData) onUpdateData();
    } catch {
      toast.error(t("complexTender.submitBid.rejectTenderError"));
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <div className={style.card}>
      <div className={style.header}>
        <span className={style.title}>
          {t(order?.assignmentMethod === "Auction" ? "complexTender.auctionTitle" : "complexTender.title")}
        </span>
        {order?.tenderDateEnd && (
          <span className={style.deadline}><TenderDeadlineText date={order.tenderDateEnd} /></span>
        )}
      </div>

      <div className={style.summary}>
        <div className={style.summary_item}>
          <span className={style.summary_label}>
            {t("complexTender.submitBid.vehiclesNeeded", { context: transportContext })}
          </span>
          <span className={style.summary_value}>
            {t("complexTender.units.pieces", { count: maxVehiclesCount || 0 })}
          </span>
        </div>
        <div className={style.summary_item}>
          <span className={style.summary_label}>{t("complexTender.submitBid.loadingWindow")}</span>
          <span className={style.summary_value}>{shippingWindow}</span>
        </div>
      </div>

      <div className={style.price_row}>
        <CustomInputCurrency
          value={price}
          isBgGray
          inputType="number"
          labelText={t("complexTender.submitBid.yourPriceLabel", { context: transportContext })}
          placeholder={t("complexTender.submitBid.yourPricePlaceholder")}
          errorText={errorText}
          currency={currency}
          lockCurrency={lockCurrency}
          onChangeValue={(value) => {
            setPrice(value);
            setErrorText("");
          }}
          onChangeCurrency={setCurrency}
        />
        {lockCurrency && (
          <span className={style.currency_hint}>{t("orderInfoExpeditor.suggestPrice.currencyLockedHint")}</span>
        )}
      </div>

      <div className={style.details_row}>
        <div className={style.vehicles_control}>
          <button
            type="button"
            className={style.counter_button}
            onClick={decrementVehicles}
            disabled={vehiclesCount <= 0 || isSubmitting}
            aria-label={t("complexTender.submitBid.decreaseVehicles", { context: transportContext })}
          >
            -
          </button>
          <div className={style.counter_value_box}>
            <label className={style.counter_label}>
              {t("complexTender.submitBid.readyVehicles", { context: transportContext })}
            </label>
            <input
              className={style.counter_input}
              type="number"
              value={vehiclesCount}
              min="0"
              max={maxVehiclesCount || undefined}
              onChange={(event) => {
                const nextValue = Number(event.target.value) || 0;
                setVehiclesCount(maxVehiclesCount ? Math.min(maxVehiclesCount, nextValue) : nextValue);
              }}
              onWheel={(event) => event.target.blur()}
            />
          </div>
          <button
            type="button"
            className={style.counter_button}
            onClick={incrementVehicles}
            disabled={(maxVehiclesCount && vehiclesCount >= maxVehiclesCount) || isSubmitting}
            aria-label={t("complexTender.submitBid.increaseVehicles", { context: transportContext })}
          >
            +
          </button>
        </div>
        <div className={style.days_input}>
          <CustomInput
            value={plannedDays}
            isBgGray
            inputType="text"
            labelText={`${t("complexTender.submitBid.plannedDaysLabel")}${transitDaysRequired ? "*" : ""}`}
            placeholder={t("complexTender.submitBid.plannedDaysPlaceholder")}
            errorText={plannedDaysErrorText}
            onChangeValue={(value) => {
              setPlannedDays(value.replace(/[^0-9-]/g, ""));
              setPlannedDaysErrorText("");
            }}
          />
        </div>
      </div>

      <CustomTextarea
        value={comments}
        isBgGray
        rows={1}
        resize="none"
        labelText={t("complexTender.submitBid.commentLabel")}
        placeholder={t("complexTender.submitBid.commentPlaceholder")}
        onChangeValue={setComments}
      />

      <div className={style.buttons_row}>
        <div className={style.primary_button}>
          <CustomButton
            text={t("complexTender.submitBid.submit")}
            isNavy
            onClick={handleSuggestClick}
            isLoading={isSubmitting}
            disabled={isSubmitting}
            style={{ width: "100%" }}
          />
        </div>
        <div className={style.secondary_button}>
          <CustomButton
            text={t("complexTender.submitBid.reject")}
            isNavy
            isWhite
            onClick={() => setShowRejectModal(true)}
            disabled={isSubmitting}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      <ConfirmActionModal
        isOpen={showConfirmModal}
        title={t("complexTender.submitBid.confirmTitle")}
        message={t("complexTender.submitBid.confirmMessage", { context: transportContext })}
        confirmText={t("common.yes")}
        cancelText={t("common.no")}
        onConfirm={handleConfirmSend}
        onCancel={() => setShowConfirmModal(false)}
        isLoading={isSubmitting}
      />

      <ConfirmActionModal
        isOpen={showRejectModal}
        title={t("complexTender.submitBid.rejectTenderTitle")}
        message={t("complexTender.submitBid.rejectTenderMessage")}
        confirmText={t("complexTender.submitBid.reject")}
        cancelText={t("common.cancel")}
        onConfirm={handleConfirmReject}
        onCancel={() => setShowRejectModal(false)}
        isLoading={isRejecting}
      />
    </div>
  );
};

export default ComplexOrderBids;
